import type { ApiBid } from "../mappers/types";

export interface CellChangeResult {
    changed: boolean;
    updates: {
        type: "line_item" | "line_group";
        id: number;
        field: string;
        val: any;
    }[];
    actions: string[];
    addonPopupGroupId?: number;
    addonPopupData?: any;
}

// ── Column Field Defaults ──────────────────────────────────────────────────

/** Default values for fields when a cell is cleared (e.g. backspace). Prevents NotNullViolation errors. */
const NUMERIC_FIELD_DEFAULT = 0;
const TEXT_FIELD_DEFAULT = "";

// ── Fields that map directly from columnId → API field name ────────────────

/** Numeric columns that are directly editable on a line item */
const LINE_ITEM_NUMERIC_FIELDS = new Set([
    "vendor_rate",
    "markup",
    "markup_amount",
    "budget_buffer",
    "budget_buffer_amount",
    "quantity",
    "days",
    "adjustment",
    "ot_1_5",
    "ot_2_0",
    "internal_rate",
    "internal_markup",
]);

/** Text columns that are directly editable on a line item */
const LINE_ITEM_TEXT_FIELDS = new Set(["external_notes", "internal_notes"]);

// ── Helpers ────────────────────────────────────────────────────────────────

/** Coerce a cell value to a safe non-null default for its type. */
function safeNumericValue(value: any): number {
    if (
        value === null ||
        value === undefined ||
        value === "" ||
        Number.isNaN(Number(value))
    ) {
        return NUMERIC_FIELD_DEFAULT;
    }
    return Number(value);
}

function safeTextValue(value: any): string {
    if (value === null || value === undefined) {
        return TEXT_FIELD_DEFAULT;
    }
    return String(value);
}

/** Resolve the API field name from the columnId, stripping running prefix if present. */
function resolveFieldName(columnId: string): string {
    const runningPrefix = "line_item_running.";
    return columnId.startsWith(runningPrefix)
        ? columnId.slice(runningPrefix.length)
        : columnId;
}

// ── Core ───────────────────────────────────────────────────────────────────

/**
 * Applies cell data changes to the bid tree.
 *
 * This function handles ONLY data mutations (text edits, number changes, dropdown selections).
 * Non-data actions (delete, copy, addon, settings) are handled by the ChevronRowAction
 * callback and should NOT flow through this function.
 */
export function applyCellChangesToBid(
    currentBid: ApiBid,
    changes: any[],
): { newBid: ApiBid; result: CellChangeResult } {
    const newBid: ApiBid = structuredClone(currentBid);
    const result: CellChangeResult = {
        changed: false,
        updates: [],
        actions: [],
    };

    for (const change of changes) {
        const { rowId, columnId, newCell } = change;
        const rid = rowId as string;
        const field = resolveFieldName(columnId);

        if (rid.startsWith("item-")) {
            applyLineItemChange(newBid, rid, field, newCell, result);
        } else if (rid.startsWith("group-")) {
            applyLineGroupChange(newBid, rid, field, newCell, result);
        } else if (rid.startsWith("addon-")) {
            applyAddonChange(newBid, rid, field, newCell, result);
        }
    }

    return { newBid, result };
}

// ── Line Item Changes ──────────────────────────────────────────────────────

function applyLineItemChange(
    bid: ApiBid,
    rowId: string,
    field: string,
    newCell: any,
    result: CellChangeResult,
): void {
    const itemId = parseInt(rowId.replace("item-", ""));
    const items: any[] =
        (bid as any).line_items ?? (bid as any).line_items_running ?? [];
    const item = items.find((i: any) => i.id === itemId);
    if (!item) return;

    // Generic numeric field
    if (LINE_ITEM_NUMERIC_FIELDS.has(field) && newCell.type === "number") {
        const val = safeNumericValue(newCell.value);
        item[field] = val;
        result.changed = true;
        result.updates.push({ type: "line_item", id: itemId, field, val });
        return;
    }

    // Percentage fields: the PercentageCellTemplate already converts user input (80) → decimal (0.8)
    // so newCell.value is already the decimal fraction ready to store.
    if (LINE_ITEM_NUMERIC_FIELDS.has(field) && newCell.type === "percentage") {
        const val = safeNumericValue(newCell.value);
        item[field] = val;
        result.changed = true;
        result.updates.push({ type: "line_item", id: itemId, field, val });
        return;
    }

    // Generic text field
    if (LINE_ITEM_TEXT_FIELDS.has(field) && newCell.type === "text") {
        const val = safeTextValue(newCell.text);
        item[field] = val;
        result.changed = true;
        result.updates.push({ type: "line_item", id: itemId, field, val });
        return;
    }

    // Special-case fields with non-standard cell types
    switch (field) {
        case "name": {
            if (newCell.type === "chevron") {
                const val = safeTextValue(newCell.text);
                item.name = val;
                result.changed = true;
                result.updates.push({
                    type: "line_item",
                    id: itemId,
                    field: "name",
                    val,
                });
            }
            break;
        }
        case "is_internal": {
            if (newCell.type === "dropdown") {
                const val = newCell.value === "Internal";
                item.is_internal = val;
                result.changed = true;
                result.updates.push({
                    type: "line_item",
                    id: itemId,
                    field: "is_internal",
                    val,
                });
            }
            break;
        }
        case "rate_type": {
            if (newCell.type === "dropdown") {
                item.rate_type = newCell.value;
                result.changed = true;
                result.updates.push({
                    type: "line_item",
                    id: itemId,
                    field: "rate_type",
                    val: newCell.value,
                });
            }
            break;
        }
        case "vendor_currency": {
            if (newCell.type === "dropdown") {
                item.vendor_currency = newCell.value;
                result.changed = true;
                result.updates.push({
                    type: "line_item",
                    id: itemId,
                    field: "vendor_currency",
                    val: newCell.value,
                });
            }
            break;
        }
        case "vendor_rate": {
            // Handle both dynamicRate (service item select) and plain number edit
            if (newCell.type === "dynamicRate") {
                item.service_item_id = newCell.value.id;
                result.changed = true;
                result.updates.push({
                    type: "line_item",
                    id: itemId,
                    field: "service_item_id",
                    val: newCell.value.id,
                });
            } else if (newCell.type === "number") {
                const val = safeNumericValue(newCell.value);
                item.vendor_rate = val;
                result.changed = true;
                result.updates.push({
                    type: "line_item",
                    id: itemId,
                    field: "vendor_rate",
                    val,
                });
            }
            break;
        }
    }
}

// ── Line Group Changes ─────────────────────────────────────────────────────

function applyLineGroupChange(
    bid: ApiBid,
    rowId: string,
    field: string,
    newCell: any,
    result: CellChangeResult,
): void {
    const groupId = parseInt(rowId.replace("group-", ""));
    const groups: any[] = bid.line_groups ?? [];
    const group = groups.find((g: any) => g.id === groupId);
    if (!group) return;

    if (field === "name" && newCell.type === "chevron") {
        const val = safeTextValue(newCell.text);
        group.name = val;
        result.changed = true;
        result.updates.push({
            type: "line_group",
            id: groupId,
            field: "name",
            val,
        });
    }
}

// ── Addon Changes ──────────────────────────────────────────────────────────

function applyAddonChange(
    bid: ApiBid,
    rowId: string,
    field: string,
    newCell: any,
    result: CellChangeResult,
): void {
    const addonId = parseInt(rowId.replace("addon-", ""));
    const addon = bid.addons?.find((a: any) => a.id === addonId);
    if (!addon) return;

    if (field === "name" && newCell.type === "chevron") {
        addon.name = safeTextValue(newCell.text);
        result.changed = true;
        // Addon name updates go through a different API path
    }
}
